import { ScormCard } from '@/features/Scorm';
import { FileDownloadRow } from '@/shared/ui/FileDownloadRow';
import { Section } from '@/shared/ui/Section';
import type { IContentStep, IStudentStepScorm } from '@/shared/types/lesson';
import { LessonVideo } from '../LessonVideo';
import { getLessonStepTitle } from './stepTheme';

export type LessonStepScormMode = 'launch' | 'preview';

type StepPartsProps = {
  step: IContentStep;
  scormMode: LessonStepScormMode;
  onScormAction?: () => void;
  isScormLoading?: boolean;
};

type LessonStepContentProps = StepPartsProps & {
  stepId: string;
};

const getStepScormSession = (scorm: IContentStep['scorm']) =>
  scorm && 'session' in scorm ? (scorm as IStudentStepScorm).session : null;

const StepText = ({ step }: { step: IContentStep }) =>
  step.content ? (
    <p className="whitespace-pre-wrap text-base font-normal leading-[140%] text-black">
      {step.content}
    </p>
  ) : null;

/**
 * Видео ещё не готово или пакет SCORM в обработке либо с ошибкой: шаг приходит без video / scorm.
 * Причину не называем — студент на неё не влияет
 */
const StepUnavailable = ({ title }: { title: string }) => (
  <div className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-grey-100 bg-grey-10 px-6 py-10 text-center">
    <p className="text-base font-medium leading-[120%] text-black-pure">{title}</p>
    <p className="text-sm font-normal leading-[140%] text-grey-secondary">
      It will appear here once it&apos;s ready. Please check back later.
    </p>
  </div>
);

const StepVideo = ({ step }: { step: IContentStep }) => {
  if (step.type !== 'video') return null;
  return step.video?.embedUrl ? (
    <LessonVideo video={step.video} />
  ) : (
    <StepUnavailable title="Video is not available yet" />
  );
};

const StepScorm = ({ step, scormMode, onScormAction, isScormLoading }: StepPartsProps) => {
  if (step.type !== 'scorm') return null;
  return step.scorm ? (
    <ScormCard
      scorm={{ ...step.scorm, session: getStepScormSession(step.scorm) }}
      mode={scormMode}
      onAction={() => onScormAction?.()}
      isLoading={isScormLoading}
    />
  ) : (
    <StepUnavailable title="Interactive content is not available yet" />
  );
};

const StepFiles = ({ step }: { step: IContentStep }) =>
  step.attachments.length > 0 ? (
    <div className="grid gap-3 sm:grid-cols-2">
      {step.attachments.map((file) => (
        <FileDownloadRow key={file.key} file={file} />
      ))}
    </div>
  ) : null;

export const LessonStepContent = ({ stepId, ...props }: LessonStepContentProps) => (
  <div id={stepId} data-lesson-step-order={props.step.order} className="scroll-mt-28">
    <Section className="relative flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h5 className="text-[24px] font-medium leading-[120%] text-black-pure">
          Step {props.step.order}
        </h5>
        <div className="flex flex-col gap-2">
          <p className="text-base font-medium leading-[120%] text-black-pure">
            {getLessonStepTitle(props.step)}
          </p>
          <StepText step={props.step} />
        </div>
      </div>
      <StepVideo step={props.step} />
      <StepScorm {...props} />
      <StepFiles step={props.step} />
    </Section>
  </div>
);

export const LessonSingleStep = (props: StepPartsProps) => {
  const { step } = props;
  const hasTextOrFiles = Boolean(step.content) || step.attachments.length > 0;

  return (
    <>
      {step.type === 'video' && (
        <div className="w-full">
          <StepVideo step={step} />
        </div>
      )}
      <StepScorm {...props} />
      {hasTextOrFiles && (
        <Section className="flex flex-col gap-4">
          <StepText step={step} />
          <StepFiles step={step} />
        </Section>
      )}
    </>
  );
};
